import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { AccessibilityCheckRow, ImportResult } from '../upload-accessibility-checks.component';
import { BackendAccessibilityChecksRepo } from '../../../../services/api/support/admin/backend-accessibility-checks.repo';
import { AccessibilityCheckModel } from '../../../../services/api/support/models/accessibility-check-model';

@Component({
    selector: 'app-upload-progress',
    imports: [CommonModule],
    templateUrl: './upload-progress.component.html',
    styleUrls: ['./upload-progress.component.scss'],
})
export class UploadProgressComponent implements OnInit {
    @Input() data: AccessibilityCheckRow[] = [];

    private repo = inject(BackendAccessibilityChecksRepo);
    @Output() complete = new EventEmitter<ImportResult>();

    private progressSubject = new BehaviorSubject({ current: 0, total: 0, message: '' });
    progress$ = this.progressSubject.asObservable();

    isImporting = false;
    isCancelled = false;
    currentRow = 0;
    totalRows = 0;
    currentMessage = '';

    successful = 0;
    failed = 0;
    errors: Array<{ rowNumber: number; id: number; error: string }> = [];
    successfulUpdates: Array<{ rowNumber: number; id: number; name?: string }> = [];

    startTime = 0;
    checksMap: Map<number, AccessibilityCheckModel> = new Map();

    ngOnInit (): void {
        this.startImport();
    }

    async startImport (): Promise<void> {
        this.isImporting = true;
        this.totalRows = this.data.length;
        this.startTime = Date.now();

        this.updateProgress(0, 'Loading existing checks...');

        try {
            const allChecks: AccessibilityCheckModel[] = [];
            let page = 1;
            const pageSize = 1000;
            let hasMore = true;

            while (hasMore) {
                const pageData = await this.repo.getAll({
                    page,
                    page_size: pageSize,
                    search: '',
                });

                allChecks.push(...pageData);
                hasMore = pageData.length === pageSize;
                page++;

                this.updateProgress(0, `Loading existing checks... (${allChecks.length} loaded)`);
            }

            this.checksMap = new Map(allChecks.map(check => [check.id, check]));
            this.updateProgress(0, `Loaded ${allChecks.length} existing checks. Starting import...`);
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load checks';
            this.updateProgress(0, errorMessage);
            this.isImporting = false;
            this.complete.emit({
                total: this.totalRows,
                successful: 0,
                failed: this.totalRows,
                errors: this.data.map(row => ({
                    rowNumber: row.rowNumber,
                    id: row.id,
                    error: errorMessage,
                })),
                successfulUpdates: [],
            });
            return;
        }

        this.updateProgress(0, `Starting import of ${this.totalRows} checks...`);

        for (let i = 0; i < this.data.length; i++) {
            if (this.isCancelled) {
                this.updateProgress(i, 'Import cancelled by user');
                break;
            }

            const row = this.data[i];
            this.currentRow = i + 1;
            this.updateProgress(i + 1, `Updating check ID ${row.id}...`);

            try {
                const existingCheck = this.checksMap.get(row.id);
                if (!existingCheck) {
                    throw new Error(`Check with database ID ${row.id} not found`);
                }

                if (row.name !== undefined) {
                    existingCheck.name = row.name;
                }
                if (row.description !== undefined) {
                    existingCheck.description = row.description;
                    existingCheck.short_description = row.description;
                }
                if (row.friendly_name !== undefined) {
                    existingCheck.friendly_name = row.friendly_name;
                }
                if (row.impact !== undefined) {
                    existingCheck.impact = row.impact.toLowerCase();
                }
                if (row.difficulty !== undefined) {
                    // Parse difficulty from various formats: "1 - Level 1", "Level 1", or "1"
                    const difficultyStr = row.difficulty.toString().trim();
                    const levelMatch = difficultyStr.match(/^(\d+)(?:\s*-\s*Level\s*\d+)?$/i) || difficultyStr.match(/^Level\s*(\d+)$/i);

                    if (levelMatch) {
                        existingCheck.difficulty = parseInt(levelMatch[1], 10);
                    } else {
                        existingCheck.difficulty = parseInt(difficultyStr, 10);
                    }
                }
                if (row.article_url !== undefined) {
                    existingCheck.article_url = row.article_url;
                }

                await this.repo.update(existingCheck);
                this.successful++;

                this.successfulUpdates.push({
                    rowNumber: row.rowNumber,
                    id: row.id,
                    name: existingCheck.name,
                });
            } catch (error: unknown) {
                this.failed++;
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                this.errors.push({
                    rowNumber: row.rowNumber,
                    id: row.id,
                    error: errorMessage,
                });
            }
        }

        this.isImporting = false;
        const elapsedSeconds = Math.round((Date.now() - this.startTime) / 1000);
        this.updateProgress(
            this.currentRow,
            this.isCancelled
                ? 'Import cancelled'
                : `Import complete! ${this.successful} successful, ${this.failed} failed in ${elapsedSeconds}s`,
        );

        this.complete.emit({
            total: this.totalRows,
            successful: this.successful,
            failed: this.failed,
            errors: this.errors,
            successfulUpdates: this.successfulUpdates,
        });
    }

    updateProgress (current: number, message: string): void {
        this.currentMessage = message;
        this.progressSubject.next({
            current,
            total: this.totalRows,
            message,
        });
    }

    getProgressPercentage (): number {
        if (this.totalRows === 0) {
            return 0;
        }
        return Math.round((this.currentRow / this.totalRows) * 100);
    }

    getProgressBarColor (): string {
        const percentage = this.getProgressPercentage();
        if (percentage < 25) {
            return 'info';
        }
        if (percentage < 50) {
            return 'primary';
        }
        if (percentage < 75) {
            return 'warning';
        }
        return 'success';
    }

    cancelImport (): void {
        this.isCancelled = true;
    }
}
